/**
 * The open online trial (docs/handoff/ONLINE_TRIAL.md): a build made with `VITE_TRIAL=1` opens on a small guest login
 * (a name, man or woman) instead of the front screens, then plays on the trial server. No account, no invite: the
 * browser keeps a random key of its own, so the same browser comes back to the same character (the server keeps it;
 * the name is made unique there). Every character holds every art (server, apps/server/src/trial.ts).
 *
 * Where the server is: `VITE_TRIAL_SERVER` baked into the build, else `trial.json` beside the page
 * (`{"server":"wss://…"}`), so a new tunnel address needs no rebuild (deploy/trial/set-server.sh). Development: open
 * the dev game with `?trial=ws://localhost:8787` to try the same screen against a local `TRIAL=1` server.
 * `?offline` on a trial build skips all this and plays alone, as does the screen's own "เล่นออฟไลน์" button.
 */
import { defaultAppearance, ZONES, type PlayerProgress, type Sex, type ZoneId } from '@nine/shared';
import { newPlayer } from '../newPlayer';
import { session, setSession, stopLocalSaves } from '../storage';
import type { HelloInfo } from './netWorld';

export interface TrialStart {
  url: string;
  hello: HelloInfo;
  /** The replica's starting progress (the server's own arrives right after joining and replaces it). */
  progress: PlayerProgress;
}

const NAME_KEY = 'nine-swords.trial.name';
const SEX_KEY = 'nine-swords.trial.sex';
const BROWSER_KEY = 'nine-swords.trial.key';
/** Set in this tab once it entered: a reload (or a zone change) goes straight back in. */
const IN_KEY = 'nine-swords.trial.in';

const NAME_MIN = 2;
const NAME_MAX = 16;

const store = {
  get(k: string, s: Storage = localStorage): string | null {
    try {
      return s.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string, s: Storage = localStorage): void {
    try {
      s.setItem(k, v);
    } catch {
      // Private mode: kept for this page only.
    }
  },
};

/** This browser's key (32 hex digits), made once. */
function browserKey(): string {
  let k = store.get(BROWSER_KEY);
  if (!k || !/^[0-9a-f]{32}$/.test(k)) {
    k = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
    store.set(BROWSER_KEY, k);
  }
  return k;
}

/** The trial server's address, or null when this is not a trial (or it cannot be found). */
async function serverUrl(): Promise<string | null> {
  const q = new URLSearchParams(location.search);
  if (q.has('offline')) return null;
  if (import.meta.env.DEV) return q.get('trial');
  if (import.meta.env.VITE_TRIAL !== '1') return null;
  const baked = import.meta.env.VITE_TRIAL_SERVER as string | undefined;
  if (baked) return baked;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}trial.json?t=${Date.now()}`, { cache: 'no-store' });
    const j = (await res.json()) as { server?: unknown };
    return typeof j.server === 'string' ? j.server : null;
  } catch {
    return null;
  }
}

/** The server's `/health` (the zone it runs, who is on), or null when it does not answer. */
async function health(url: string): Promise<{ zone: string; players: number } | null> {
  const http = url.replace(/^ws/, 'http').replace(/\/+$/, '');
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 5000);
  try {
    const res = await fetch(`${http}/health`, { cache: 'no-store', signal: ctl.signal });
    const j = (await res.json()) as { ok?: unknown; zone?: unknown; players?: unknown; connections?: unknown };
    if (j.ok !== true || typeof j.zone !== 'string') return null;
    return { zone: j.zone, players: typeof j.players === 'number' ? j.players : Number(j.connections) || 0 };
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

/** Length in letters (Thai vowel and tone marks not counted), as the server counts it. */
const letters = (s: string) => [...s.replace(/\p{M}/gu, '')].length;

/** The guest login: resolves with the name and sex picked, or null for "play offline". */
function ask(status: () => Promise<{ zone: string; players: number } | null>): Promise<{ name: string; sex: Sex } | null> {
  return new Promise((done) => {
    const root = document.createElement('div');
    root.id = 'trialLogin';
    Object.assign(root.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '10000',
      display: 'grid',
      placeItems: 'center',
      background: 'rgba(8, 6, 5, 0.72)',
      font: '16px/1.5 system-ui, sans-serif',
      color: '#f3e7cf',
    });
    const card = document.createElement('form');
    Object.assign(card.style, {
      width: 'min(360px, calc(100vw - 32px))',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '22px 24px',
      background: 'rgba(22, 16, 12, 0.96)',
      border: '2px solid #c9a24a',
      outline: '1px solid #6b5326',
      outlineOffset: '3px',
    });
    const title = document.createElement('div');
    title.textContent = 'เล่นออนไลน์ (ทดลอง)';
    Object.assign(title.style, { fontSize: '20px', fontWeight: '700', color: '#e8c76a' });
    const info = document.createElement('div');
    info.textContent = 'กำลังตรวจเซิร์ฟเวอร์…';
    Object.assign(info.style, { fontSize: '13px', opacity: '0.85' });
    const label = document.createElement('label');
    label.textContent = 'ชื่อตัวละคร';
    Object.assign(label.style, { fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '4px' });
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 40;
    input.autocomplete = 'username';
    input.placeholder = `${NAME_MIN}–${NAME_MAX} ตัวอักษร`;
    input.value = store.get(NAME_KEY) ?? '';
    Object.assign(input.style, {
      font: 'inherit',
      padding: '8px 10px',
      color: '#fff3d8',
      background: '#120d0a',
      border: '1px solid #8a6d33',
      outline: 'none',
    });
    label.append(input);
    let sex: Sex = store.get(SEX_KEY) === 'f' ? 'f' : 'm';
    const sexRow = document.createElement('div');
    Object.assign(sexRow.style, { display: 'flex', gap: '8px' });
    const sexBtn = (s: Sex, text: string) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = text;
      b.dataset.sex = s;
      Object.assign(b.style, { flex: '1', font: 'inherit', padding: '6px', cursor: 'pointer' });
      b.addEventListener('click', () => {
        sex = s;
        paint();
      });
      return b;
    };
    const men = sexBtn('m', 'ชาย');
    const women = sexBtn('f', 'หญิง');
    sexRow.append(men, women);
    const paint = () => {
      for (const b of [men, women]) {
        const on = b.dataset.sex === sex;
        Object.assign(b.style, {
          color: on ? '#1a120c' : '#f3e7cf',
          background: on ? '#e8c76a' : 'transparent',
          border: `1px solid ${on ? '#e8c76a' : '#8a6d33'}`,
        });
      }
    };
    paint();
    const err = document.createElement('div');
    Object.assign(err.style, { minHeight: '1.2em', fontSize: '13px', color: '#ff9a8a' });
    const go = document.createElement('button');
    go.type = 'submit';
    go.textContent = 'เข้าเล่น';
    Object.assign(go.style, {
      font: 'inherit',
      fontWeight: '700',
      padding: '9px',
      color: '#1a120c',
      background: '#e8c76a',
      border: '0',
      cursor: 'pointer',
    });
    const alone = document.createElement('button');
    alone.type = 'button';
    alone.textContent = 'เล่นออฟไลน์';
    Object.assign(alone.style, {
      font: 'inherit',
      fontSize: '13px',
      padding: '4px',
      color: '#cdb98f',
      background: 'transparent',
      border: '0',
      cursor: 'pointer',
      textDecoration: 'underline',
    });
    const note = document.createElement('div');
    note.textContent = 'ทดลองเล่น: ทุกคนได้วิชาครบทุกวิชา · Enter พิมพ์คุยในโซน';
    Object.assign(note.style, { fontSize: '12px', opacity: '0.7' });
    card.append(title, info, label, sexRow, err, go, alone, note);
    root.append(card);
    document.body.append(root);
    setTimeout(() => input.focus(), 0);
    // The game's own keys must not see what is typed here.
    root.addEventListener('keydown', (e) => e.stopPropagation());
    void status().then((h) => {
      info.textContent = h
        ? `เซิร์ฟเวอร์เปิดอยู่ · ${ZONES[h.zone as ZoneId]?.name ?? h.zone} · ออนไลน์ ${h.players} คน`
        : 'ติดต่อเซิร์ฟเวอร์ไม่ได้ · ลองใหม่ภายหลัง หรือเล่นออฟไลน์';
    });
    const finish = (v: { name: string; sex: Sex } | null) => {
      root.remove();
      done(v);
    };
    card.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = input.value.normalize('NFC').replace(/\s+/g, ' ').trim();
      const n = letters(name);
      if (n < NAME_MIN || n > NAME_MAX) {
        err.textContent = `ชื่อต้องยาว ${NAME_MIN}–${NAME_MAX} ตัวอักษร`;
        return input.focus();
      }
      if (!/^[\p{L}\p{M}\p{N} _.-]+$/u.test(name)) {
        err.textContent = 'ใช้ได้เฉพาะตัวอักษร ตัวเลข เว้นวรรค และ _ - .';
        return input.focus();
      }
      store.set(NAME_KEY, name);
      store.set(SEX_KEY, sex);
      finish({ name, sex });
    });
    alone.addEventListener('click', () => finish(null));
  });
}

/**
 * Before the game boots: on a trial build (or `?trial=` in development), the guest login, then what the boot needs to
 * play online. Null: play as always (not a trial, or the player chose to play offline).
 */
export async function trialLogin(): Promise<TrialStart | null> {
  const url = await serverUrl();
  if (!url || !/^wss?:\/\//.test(url)) return null;
  // Chose to play offline earlier in this tab: zone changes and reloads stay offline.
  if (session().enter && store.get(IN_KEY, sessionStorage) === '0') return null;
  let status: Promise<{ zone: string; players: number } | null> | null = null;
  const probe = () => (status ??= health(url));
  const again = session().enter && store.get(IN_KEY, sessionStorage) === '1' && store.get(NAME_KEY);
  const picked = again
    ? { name: store.get(NAME_KEY)!, sex: (store.get(SEX_KEY) === 'f' ? 'f' : 'm') as Sex }
    : await ask(probe);
  if (!picked) {
    store.set(IN_KEY, '0', sessionStorage);
    return null;
  }
  const h = await probe();
  const progress = newPlayer();
  progress.name = picked.name;
  progress.appearance = defaultAppearance(picked.sex);
  // The replica is built in the zone the server runs (its /health says which).
  if (h && h.zone in ZONES) progress.location = h.zone as ZoneId;
  store.set(IN_KEY, '1', sessionStorage);
  // The server keeps this character: the browser's own single-player saves stay untouched.
  stopLocalSaves();
  setSession({ user: picked.name, enter: true });
  return { url, progress, hello: { char: browserKey(), name: picked.name, look: progress.appearance } };
}

/** Whether this page was built for (or opened as) the online trial: no stand-in duel rival then (main.ts). */
export function trialWanted(): boolean {
  const q = new URLSearchParams(location.search);
  if (q.has('offline')) return false;
  return import.meta.env.DEV ? q.has('trial') : import.meta.env.VITE_TRIAL === '1';
}
